import { useCallback, useEffect, useMemo, useState } from 'react'
import { Search, Loader2, X, Plus, Layers, AlertTriangle, BadgePercent } from 'lucide-react'
import { apiGet } from '../lib/api.js'
import { usePersistedState } from '../lib/persist.js'
import { useMe } from '../lib/me.js'
import { canEditPromotion } from '../lib/role.js'
import { Segmented } from './statistic/statShared.jsx'
import SearchableSelect from '../components/SearchableSelect.jsx'
import PromotionDrawer from '../components/promotion/PromotionDrawer.jsx'
import {
  activeInfo, readPattern, storedMax, patternTotal, maxMismatch,
  filterPromotions, typeOptions, typeCatalog, byCatalogOrder, indexBillingTypes,
  baht, repeatLabel, btLabel, NO_TYPE,
} from '../lib/promotion.js'

// Manager › ตั้งค่าโปรโมชั่น — the promotion catalog and its billing patterns.
//
// ONE fetch of the whole catalog (~224 rows), then search / type / active filter in memory,
// same as Manager › ทันตแพทย์: Xano's promotion table has no search and no active filter,
// so a round trip per keystroke would be slower AND less capable.
//
// Not cachedGet(): this page WRITES what it reads, and a memoised promise would hand the
// pre-edit list back for the rest of the session. It refetches on mount and patches rows
// in place after a save.
//
// Manager reads. GM / Admin add, edit and retire — see lib/role.js#canEditPromotion.
const ACTIVE_OPTS = [
  { v: 'true',  label: 'ใช้งาน' },
  { v: 'false', label: 'ปิดใช้งาน' },
  { v: '',      label: 'ทั้งหมด' },
]

export default function Promotion() {
  const me = useMe()
  const mayEdit = canEditPromotion(me)

  const [all, setAll] = useState([])
  const [billingTypes, setBillingTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(null)      // the promotion whose drawer is open
  const [creating, setCreating] = useState(false)

  const [search, setSearch] = usePersistedState('promotion:search', '')
  const [mains, setMains] = usePersistedState('promotion:main', () => new Set())
  const [subs, setSubs] = usePersistedState('promotion:sub', () => new Set())
  const [active, setActive] = usePersistedState('promotion:active', 'true')

  const load = useCallback(() => {
    setLoading(true)
    return Promise.all([
      apiGet('/api/promotion-manage'),
      // Billing types name each step and supply the price a step falls back to. Failing to
      // load them must not blank the page — the list still reads, steps just show an id.
      apiGet('/api/billing-types').catch(() => []),
    ])
      .then(([rows, bts]) => {
        setAll(Array.isArray(rows) ? rows : (rows?.items || []))
        setBillingTypes(Array.isArray(bts) ? bts : (bts?.items || []))
        setErr('')
      })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const btById = useMemo(() => indexBillingTypes(billingTypes), [billingTypes])
  // ประเภท / หมวดย่อย are free text, so the drawer's pickers offer what the catalog already
  // uses AND accept a new value — there is no enum to add one to.
  const types = useMemo(() => typeCatalog(all), [all])

  // Counts follow the ACTIVE filter but not search — the number beside an option answers
  // "how many promotions would this give me", not "how many match what I already typed".
  const pool = useMemo(() => filterPromotions(all, { active }), [all, active])
  const mainOpts = useMemo(() => typeOptions(pool, 'Maintype'), [pool])
  // หมวดย่อย narrows to the chosen ประเภท, or the dropdown lists every Subtype in the
  // clinic and most of them can never match.
  const subOpts = useMemo(
    () => typeOptions(mains.size ? filterPromotions(pool, { mains }) : pool, 'Subtype'),
    [pool, mains],
  )

  // A chosen value that leaves the pool (e.g. after switching to ปิดใช้งาน) would filter
  // everything to nothing with no visible chip to unset.
  useEffect(() => {
    if (!mains.size) return
    const live = new Set(mainOpts.map((o) => o.value))
    const kept = [...mains].filter((v) => live.has(v))
    if (kept.length !== mains.size) setMains(new Set(kept))
  }, [mainOpts]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!subs.size) return
    const live = new Set(subOpts.map((o) => o.value))
    const kept = [...subs].filter((v) => live.has(v))
    if (kept.length !== subs.size) setSubs(new Set(kept))
  }, [subOpts]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(
    () => filterPromotions(all, { q: search, mains, subs, active }).sort(byCatalogOrder),
    [all, search, mains, subs, active],
  )

  const hasFilter = search || mains.size > 0 || subs.size > 0 || active !== 'true'
  function clearAll() { setSearch(''); setMains(new Set()); setSubs(new Set()); setActive('true') }

  // A saved row is patched in place rather than refetched — the drawer already holds the
  // server's own response, so re-reading would only cost a round trip.
  function onSaved(updated) {
    if (!updated?.id) return
    setAll((prev) => {
      const i = prev.findIndex((x) => Number(x.id) === Number(updated.id))
      return i < 0 ? [...prev, updated] : prev.map((x, j) => (j === i ? { ...x, ...updated } : x))
    })
    setOpen((prev) => (prev && Number(prev.id) === Number(updated.id) ? { ...prev, ...updated } : prev))
  }

  return (
    <div className="px-10 py-8 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">โปรโมชั่น</h1>
        {mayEdit && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 shrink-0"
          >
            <Plus size={15} /> เพิ่มโปรโมชั่น
          </button>
        )}
      </div>
      <p className="text-[13.5px] text-slate-500 mb-4">
        รายการโปรโมชั่นทั้งหมด ราคา ค่ามือหมอ และรูปแบบการเก็บเงิน (pattern) · กดที่แถวเพื่อดูรายละเอียด
        {!mayEdit && <span className="text-slate-400"> · บัญชีนี้ดูได้อย่างเดียว แก้ไขได้เฉพาะ GM และ Admin</span>}
      </p>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อโปรโมชั่น / ประเภท / รายละเอียด…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10"
          />
        </div>
        <SearchableSelect
          multiple
          className="w-[210px]"
          options={mainOpts}
          values={mains}
          onChange={setMains}
          placeholder="ประเภททั้งหมด"
          searchPlaceholder="ค้นหาประเภท…"
        />
        <SearchableSelect
          multiple
          className="w-[210px]"
          options={subOpts}
          values={subs}
          onChange={setSubs}
          placeholder="หมวดย่อยทั้งหมด"
          searchPlaceholder="ค้นหาหมวดย่อย…"
        />
        <Segmented value={active} onChange={setActive} options={ACTIVE_OPTS} />
        {hasFilter && (
          <button onClick={clearAll} className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
      </div>

      {(mains.size > 0 || subs.size > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {[...mains].map((v) => (
            <Chip key={`m-${v}`} label={v === NO_TYPE ? 'ไม่ระบุประเภท' : v}
              onRemove={() => setMains((p) => { const n = new Set(p); n.delete(v); return n })} />
          ))}
          {[...subs].map((v) => (
            <Chip key={`s-${v}`} label={v === NO_TYPE ? 'ไม่ระบุหมวดย่อย' : v}
              onRemove={() => setSubs((p) => { const n = new Set(p); n.delete(v); return n })} />
          ))}
        </div>
      )}

      <div className="text-[13px] text-slate-500 mb-2">
        แสดง <span className="font-semibold text-slate-700 tabular-nums">{rows.length.toLocaleString()}</span> จาก {all.length.toLocaleString()} รายการ
      </div>

      {/* table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5">โปรโมชั่น</th>
                <th className="px-4 py-2.5">ประเภท</th>
                <th className="px-4 py-2.5">รูปแบบการเก็บเงิน</th>
                <th className="px-4 py-2.5 text-right whitespace-nowrap">มูลค่ารวม</th>
                <th className="px-4 py-2.5 whitespace-nowrap w-px">คอมมิชชั่น</th>
                <th className="px-4 py-2.5 whitespace-nowrap w-px">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-rose-500">{err}</td></tr>
              )}
              {!loading && !err && !rows.length && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">ไม่พบโปรโมชั่นที่ตรงกับตัวกรอง</td></tr>
              )}
              {!loading && !err && rows.map((p) => (
                <Row key={p.id} p={p} btById={btById} onOpen={() => setOpen(p)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <PromotionDrawer
          row={open}
          billingTypes={billingTypes}
          types={types}
          onClose={() => setOpen(null)}
          onSaved={onSaved}
        />
      )}
      {creating && (
        <PromotionDrawer
          create
          billingTypes={billingTypes}
          types={types}
          onClose={() => setCreating(false)}
          onSaved={(created) => { onSaved(created); setCreating(false); setOpen(created) }}
        />
      )}
    </div>
  )
}

function Chip({ label, onRemove }) {
  return (
    <button
      onClick={onRemove}
      className="inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-brand-50 px-3 py-1 text-[12.5px] font-medium text-brand-700"
    >
      {label}
      <X size={12} className="text-brand-400" />
    </button>
  )
}

function Row({ p, btById, onOpen }) {
  const a = activeInfo(p.active === true)
  // readPattern, not the raw array: the stored order is not the charging order on #201, and
  // the stored `repeat` is wrong on 23 rows — both would show a schedule nobody is billed by.
  const steps = readPattern(p)
  const max = storedMax(p)
  const total = patternTotal(steps)
  const bad = maxMismatch(p)

  return (
    <tr onClick={onOpen} className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition">
      <td className="px-4 py-3">
        <div className="font-medium text-slate-800">{p.Name || <span className="text-slate-300">(ไม่มีชื่อ)</span>}</div>
        {p.Detail && (
          <div className="text-[12px] text-slate-400 line-clamp-1 max-w-[340px]" title={p.Detail}>
            {String(p.Detail).replace(/\s*\n\s*/g, ' · ')}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {p.Maintype && <span className="inline-flex items-center rounded-md bg-brand-50 text-brand-700 px-1.5 py-0.5 text-[11px]">{p.Maintype}</span>}
          {p.Subtype && p.Subtype !== p.Maintype && (
            <span className="inline-flex items-center rounded-md bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[11px]">{p.Subtype}</span>
          )}
          {!p.Maintype && !p.Subtype && <span className="text-slate-300">—</span>}
        </div>
      </td>
      {/* The steps themselves, not a count — a manager scanning the catalog wants to see
          HOW a promotion bills. Three fit; the rest fold into +N (full list on hover). */}
      <td className="px-4 py-3">
        {steps.length ? (
          <div className="flex flex-wrap items-center gap-1" title={steps.map((s, i) => `${i + 1}. ${btLabel(btById.get(Number(s.billing_type_id))) || `#${s.billing_type_id}`} ${repeatLabel(s.repeat)}`).join(' · ')}>
            <Layers size={11} className="text-slate-400 shrink-0" />
            {steps.slice(0, 3).map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-md bg-slate-50 ring-1 ring-slate-200 text-slate-600 px-1.5 py-0.5 text-[11px]">
                {btLabel(btById.get(Number(s.billing_type_id))) || `#${s.billing_type_id}`}
                {Number(s.repeat) > 1 && <span className="text-slate-400 tabular-nums">{repeatLabel(s.repeat)}</span>}
              </span>
            ))}
            {steps.length > 3 && <span className="text-[11px] text-slate-400">+{steps.length - 3}</span>}
            <span className="text-[11px] text-slate-400 tabular-nums ml-0.5">รวม {max} ครั้ง</span>
            {bad && (
              <span title={`max ที่บันทึกไว้ = ${bad.stored} แต่ pattern คำนวณได้ ${bad.computed} — ใบเสร็จจะพิมพ์ตัวหารผิด`}>
                <AlertTriangle size={12} className="text-amber-500" />
              </span>
            )}
          </div>
        ) : <span className="text-slate-300">— ยังไม่ได้ตั้ง pattern</span>}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">
        {total > 0 ? `฿${baht(total)}` : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-4 py-3 whitespace-nowrap w-px">
        {p.commission ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200 px-2 py-0.5 text-[11.5px] font-medium tabular-nums">
            <BadgePercent size={11} /> ฿{baht(p.commission_price)}
          </span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-4 py-3 whitespace-nowrap w-px">
        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${a.tone}`}>{a.label}</span>
      </td>
    </tr>
  )
}
